// src/components/PhaseDetails/PhaseDetails.tsx
// Migrated from SpreadSheet.js (PhaseDetailsView legacy component).
// Based on GridView.tsx — shares the same Handsontable infrastructure.
// Key differences from GridView:
//  - Data endpoint: GetPhaseDetailsConversations (vs GetNewGridConversations)
//  - Each row has ConversationPhaseId (one conversation may appear as multiple rows)
//  - Saving custom fields uses CoreTaskPhaseId instead of CoreTaskId
//  - onOpenInfoWindow receives the full ConversationDetail (including ConversationPhaseId)

import {
  useRef,
  useMemo,
  useCallback,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import ExcelJS from 'exceljs';
import { HotTable, HotColumn } from '@handsontable/react';
import type { HotTableClass } from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import '@components/GridView/GridView.css';
import './PhaseDetails.css';

import { GridViewContext } from '@components/GridView/GridViewContext';
import { GridTitleCell } from '@components/GridView/renderers/GridTitleCell';
import { GridCellEditor } from '@components/GridView/renderers/GridCellEditor';
import { buildColumnHeader } from '@components/GridView/GridColumnHeader';
import {
  useGridColumns,
  isCellEnabled,
  isCellSummatory,
  isPhaseAggregateCell,
} from '@components/GridView/hooks/useGridColumns';
import { useHiddenColumns, getColumnApiKey } from '@components/GridView/hooks/useHiddenColumns';
import { GridContextMenu } from '@components/GridView/GridContextMenu';
import { ManageColumnsModal } from '@components/GridView/ManageColumnsModal';
import { gridViewService } from '@components/GridView/service';
import { socketService } from '@/services/socketService';
import {
  GRID_LICENSE_KEY,
  GRID_TITLE_COLUMN_WIDTH,
  GRID_DEFAULT_COLUMN_WIDTH,
  GRID_EDITABLE_FIELD_TYPES,
  GRID_HEADER_HEIGHT,
  FIELD_TYPES,
} from '@components/GridView/types';
import type {
  GridColumn,
  EditingCellState,
  GridCellRect,
  SaveMultipleFieldRequest,
} from '@components/GridView/types';
import type { FieldOption } from '@components/GridView/service';

import { usePhaseData } from './hooks/usePhaseData';
import { GridViewSkeleton } from '@components/GridView/GridViewSkeleton';
import DuplicateCard from '@/components/DuplicateCard/DuplicateCard';
import type { DuplicateCardRef } from '@/components/DuplicateCard/DuplicateCard';
import type { PhaseDetailsProps, PhaseDetailsRef, PhaseRow } from './types';
import type { ConversationDetail } from '@/types/conversation';
import { authStorage } from '@/lib/storage';
import { sanitizeSheetName } from '@/lib/utils';
import { config as envConfig } from '@/config/env';
import { conversationBus } from '@/lib/conversationBus';
import { checkAdminPermissions } from '@components/utils/CorgeeHelper';
import type { ActionMenuConfig } from '@components/CalendarView/types';

registerAllModules();

// ==================== Sort State ====================

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

function compareCell(
  av: string,
  bv: string,
  fieldTypeId: number | undefined,
  dir: 'asc' | 'desc',
): number
{
  const aEmpty = av === '';
  const bEmpty = bv === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let cmp = 0;
  if (fieldTypeId === FIELD_TYPES.DATE)
  {
    const da = new Date(av).getTime();
    const db = new Date(bv).getTime();
    cmp = !isNaN(da) && !isNaN(db) ? da - db : av.localeCompare(bv);
  }
  else if (
    fieldTypeId === FIELD_TYPES.INTEGER ||
    fieldTypeId === FIELD_TYPES.DECIMAL ||
    fieldTypeId === FIELD_TYPES.INTEGER_ALT
  )
  {
    const na = parseFloat(av);
    const nb = parseFloat(bv);
    cmp = !isNaN(na) && !isNaN(nb) ? na - nb : av.localeCompare(bv);
  }
  else
  {
    cmp = av.localeCompare(bv, undefined, { sensitivity: 'base' });
  }
  return dir === 'asc' ? cmp : -cmp;
}

// ==================== Row builder ====================

function formatDate(dateStr: string): string
{
  try
  {
    const d = new Date(dateStr);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
  }
  catch
  {
    return dateStr;
  }
}

/**
 * Builds a PhaseRow from a ConversationDetail.
 * Extends the base GridRow with conversationPhaseId and coreTaskPhaseId
 * so phase-specific save logic can use the correct CoreTask ID.
 */
function buildPhaseRow(conv: ConversationDetail, _columns: GridColumn[]): PhaseRow
{
  const row: PhaseRow = {
    conversationId: conv.ConversationId,
    title: conv.Title ?? '',
    icon: conv.Icon ?? '',
    isRedFlag: conv.IsRedFlag ?? false,
    isImportant: conv.IsImportant ?? null,
    createdAt: conv.CreatedAt ?? '',
    configValueId: conv.ConfigValueId ?? 0,
    'col-title': conv.Title ?? '',
    'col-created-at': conv.CreatedAt ? formatDate(conv.CreatedAt) : '',
    conversationPhaseId: conv.ConversationPhaseId ?? 0,
    coreTaskPhaseId: conv.CoreTaskPhaseId ?? conv.CoreTaskId ?? conv.ConversationId,
  };

  if (conv.LayerCustomFields)
  {
    for (const layer of conv.LayerCustomFields)
    {
      if (!layer.CustomFields) continue;
      for (const cf of layer.CustomFields)
      {
        const fieldName = (cf.FieldName ?? '').trim();
        const key = `layer-${layer.LayerId}-${fieldName.toLowerCase()}`;
        if (row[key] == null || row[key] === '')
        {
          row[key] = cf.FieldValue ?? '';
        }
      }
    }
  }

  return row;
}

// ==================== Action Menu Config ====================

function buildActionMenuConfig(): ActionMenuConfig
{
  const token = authStorage.getAccessToken() || '';
  const webUrl = import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/';
  const apiUrl = import.meta.env.VITE_JS_apiURL || 'http://localhost/Corgee.Web/';

  let storageData: Record<string, any> = {};
  try
  {
    storageData = JSON.parse(sessionStorage.getItem('C2_data') || '{}');
  }
  catch
  {
    /* ignore */
  }
  let allLayers: any[] = [];
  try
  {
    allLayers = JSON.parse(sessionStorage.getItem('C2_AllLayers') || '[]');
  }
  catch
  {
    /* ignore */
  }
  let layersToHide: any[] = [];
  try
  {
    layersToHide = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
  }
  catch
  {
    /* ignore */
  }

  return {
    api: { url: envConfig.api.baseUrl, token },
    config: { weburl: webUrl, apiurl: apiUrl } as any,
    storageData: {
      data: storageData,
      allLayers,
      layersToHide,
      layerId: parseInt(localStorage.getItem('C2_layerid') || '0'),
      conv_maxAllowedUpload: 20480000,
      enableChat: localStorage.getItem('C2_enableChat') === 'true',
      personid: parseInt(localStorage.getItem('C2_personid') || '0'),
    },
    configMenus: {
      conversation: [
        'goto-project',
        'close-infowindow',
        'opendetails-newtab',
        'mark-redflag',
        'mark-important',
        'follow-conv',
        'create-itemtype',
        'convert-to',
        'duplicate-project',
        'copylink-to-clipboard',
        'move-to',
        'copy-to',
        'submit-time',
        'edit-history',
        'edit',
        'delete',
        'duplicate-item',
        'archive',
      ],
      reply: [],
    },
    realItemTypes: {
      Users: { Name: 'People', Icon: 'icons-menu-account-box' },
      Tasks: { Name: 'Tasks', Icon: 'icons-menu-epic' },
    },
    userData: {
      imageProfile: 'Images/user.png?1.0.21.8',
      personName: 'User',
    },
  };
}

// ==================== Main Component ====================

export const PhaseDetails = forwardRef<PhaseDetailsRef, PhaseDetailsProps>(function PhaseDetails(
  {
    dashboardId,
    configValueId,
    projectId,
    filters,
    searchText,
    layersToHide,
    isActive = true,
    dashboardName,
    onOpenInfoWindow,
    onUpdateConversation,
    extraFilterParams,
  },
  ref,
)
{
  const hotRef = useRef<HotTableClass>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [editingCell, setEditingCell] = useState<EditingCellState | null>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [fillSaving, setFillSaving] = useState(false);
  const [showCopyMsg, setShowCopyMsg] = useState(false);
  const copyMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const optionsCacheRef = useRef<Map<string, FieldOption[]>>(new Map());

  useEffect(() =>
  {
    optionsCacheRef.current.clear();
  }, [dashboardId, configValueId, projectId]);

  // Measure container height via ResizeObserver
  useEffect(() =>
  {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries =>
    {
      const h = entries[0]?.contentRect.height ?? 0;
      if (h > 0) setContainerHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const {
    conversations,
    loading,
    loadingMore,
    hasMore,
    totalItems: _totalItems,
    loadMore,
    refresh,
    updateField,
    updateConversationState,
    removeConversation,
  } = usePhaseData({
    dashboardId,
    configValueId,
    projectId,
    filters,
    searchText,
    layersToHide,
    isActive,
    extraFilterParams,
  });

  const { columns, columnMap } = useGridColumns(conversations);

  const { hiddenKeys, hideColumn, showColumn, applyHiddenColumns } = useHiddenColumns({
    dashboardId,
    configValueId: 1,
    projectId,
  });

  const visibleColumns = useMemo(() =>
  {
    if (hiddenKeys.size === 0) return columns;
    return columns.filter((col, idx) =>
    {
      if (idx < 2) return true; // title + created-at always visible
      return !hiddenKeys.has(getColumnApiKey(col));
    });
  }, [columns, hiddenKeys]);

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    column: GridColumn | null;
  } | null>(null);
  const [manageColumnsOpen, setManageColumnsOpen] = useState(false);

  const rows = useMemo(
    () => conversations.map(c => buildPhaseRow(c, columns)),
    [conversations, columns],
  );

  const isAdmin = useMemo(() => checkAdminPermissions(), []);

  // ==================== Sort ====================

  const [sortState, setSortState] = useState<SortState>(null);

  const { activeRows, activeConversations } = useMemo(() =>
  {
    if (!sortState) return { activeRows: rows, activeConversations: conversations };
    const { key, dir } = sortState;
    const col = columnMap.get(key);
    const indices = conversations.map((_, i) => i);
    indices.sort((ai, bi) =>
    {
      if (key === 'col-created-at')
      {
        const av = conversations[ai]?.CreatedAt ?? '';
        const bv = conversations[bi]?.CreatedAt ?? '';
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        const da = new Date(av).getTime();
        const db = new Date(bv).getTime();
        const cmp = !isNaN(da) && !isNaN(db) ? da - db : av.localeCompare(bv);
        return dir === 'asc' ? cmp : -cmp;
      }
      const av = String(rows[ai]?.[key] ?? '');
      const bv = String(rows[bi]?.[key] ?? '');
      return compareCell(av, bv, col?.fieldTypeId, dir);
    });
    return {
      activeRows: indices.map(i => rows[i]) as PhaseRow[],
      activeConversations: indices.map(i => conversations[i]) as ConversationDetail[],
    };
  }, [rows, conversations, sortState, columnMap]);

  // ==================== Editing ====================

  // Suspend HOT's keyboard listening while the editor is open, so it doesn't swallow keys.
  const isEditorOpen = editingCell != null;
  useEffect(() =>
  {
    if (!isEditorOpen) return;
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;

    // HOT re-arms on its own mouse handling after this effect runs, so re-suppress each time.
    const suppress = () => hot.unlisten();
    hot.addHook('afterListen', suppress);
    hot.unlisten();

    return () =>
    {
      hot.removeHook('afterListen', suppress);
      if (!hot.isDestroyed) hot.listen();
    };
  }, [isEditorOpen]);

  // Enter saves and moves down one row. Explicit because the editor stops the key from
  // reaching HOT, so its own enterMoves never fires.
  const handleCommitWithEnter = useCallback(() =>
  {
    const hot = hotRef.current?.hotInstance;
    if (!hot || !editingCell) return;
    const nextRow = editingCell.visualRow + 1;
    if (nextRow >= hot.countRows()) return;
    hot.selectCell(nextRow, editingCell.visualCol);
  }, [editingCell]);

  // Asks HOT for the current <td> each time, since it recycles them while virtualising.
  const getEditingCellRect = useCallback((): GridCellRect | null =>
  {
    const hot = hotRef.current?.hotInstance;
    if (!hot || !editingCell) return null;
    const td = hot.getCell(editingCell.visualRow, editingCell.visualCol) as HTMLElement | null;
    if (!td) return null;
    const r = td.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }, [editingCell]);

  /**
   * Save a custom field value.
   * For phase rows, uses CoreTaskPhaseId instead of CoreTaskId.
   */
  const handleCellSave = useCallback(
    async (saveValue: any, displayValue?: string) =>
    {
      if (!editingCell) return;
      const { column, conversation, customField } = editingCell;
      const saved = editingCell;

      const hot = hotRef.current?.hotInstance;
      if (hot)
      {
        hot.setDataAtRowProp(
          saved.visualRow,
          saved.column.key,
          displayValue ?? saveValue,
          'save-field',
        );
      }

      setEditingCell(null);

      const actualCfId = customField?.CustomFieldId ?? column.customFieldId!;
      // Phase rows use CoreTaskPhaseId for saving; fall back to CoreTaskId or ConversationId
      const coreTaskId =
        (conversation as any).CoreTaskPhaseId ??
        conversation.CoreTaskId ??
        conversation.ConversationId;

      const ok = await gridViewService.saveFieldValue({
        ...(customField ?? {}),
        CustomFieldValueId: customField?.CustomFieldValueId ?? 0,
        CustomFieldId: actualCfId,
        CoreTaskId: coreTaskId,
        ConversationId: null,
        Value: saveValue,
        SelectedValuesText: displayValue ?? '',
        LayerId: column.layerId,
        SocketConnectionId: '',
      });

      if (ok)
      {
        updateField(
          conversation.ConversationId,
          saved.column.layerId!,
          actualCfId,
          saveValue,
          displayValue ?? String(saveValue),
          conversation.ConversationPhaseId,
        );
        conversationBus.emit('field-updated', {
          conversationId: conversation.ConversationId,
          coreTaskId: coreTaskId !== conversation.ConversationId ? coreTaskId : undefined,
          fieldName: saved.column.title,
          value: saveValue,
          layerId: saved.column.layerId ?? 0,
        });
      }
    },
    [editingCell, updateField],
  );

  const activeRowsRef = useRef(activeRows);
  activeRowsRef.current = activeRows;

  const handleRenameTitle = useCallback(async (conversationId: number, newTitle: string) =>
  {
    const ok = await gridViewService.updateConversationTitle({
      ConversationId: conversationId,
      Title: newTitle,
      SocketConnectionId: socketService.connectionId || '',
    });

    if (ok)
    {
      const hot = hotRef.current?.hotInstance;
      if (hot)
      {
        const physicalRow = activeRowsRef.current.findIndex(
          r => r?.conversationId === conversationId,
        );
        if (physicalRow >= 0)
        {
          const visualRow =
            typeof hot.toVisualRow === 'function'
              ? (hot.toVisualRow(physicalRow) ?? physicalRow)
              : physicalRow;
          hot.setDataAtRowProp(visualRow, 'col-title', newTitle, 'title-rename');
        }
      }
      conversationBus.emit('title-updated', { conversationId, title: newTitle });
    }
  }, []);

  // ==================== Red Flag / Important / Delete ====================

  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  const handleRedFlagToggle = useCallback(
    async (conversationId: number, isRedFlag: boolean) =>
    {
      const conv = conversationsRef.current.find(c => c.ConversationId === conversationId);
      if (!conv) return;
      updateConversationState(conversationId, { IsRedFlag: isRedFlag });
      const ok = await gridViewService.markRedFlag(conv, isRedFlag);
      if (!ok)
      {
        updateConversationState(conversationId, { IsRedFlag: !isRedFlag });
        return;
      }
      conversationBus.emit('redflag-updated', { conversationId, isRedFlag });
    },
    [updateConversationState],
  );

  const handleImportantToggle = useCallback(
    async (conversationId: number, isImportant: boolean | null) =>
    {
      const conv = conversationsRef.current.find(c => c.ConversationId === conversationId);
      if (!conv) return;
      updateConversationState(conversationId, { IsImportant: isImportant });
      const ok = await gridViewService.markImportant(conv, isImportant);
      if (!ok)
      {
        updateConversationState(conversationId, { IsImportant: conv.IsImportant });
        return;
      }
      conversationBus.emit('important-updated', { conversationId, isImportant });
    },
    [updateConversationState],
  );

  const handleDeleteConversation = useCallback(
    (conversationId: number) =>
    {
      removeConversation(conversationId);
      conversationBus.emit('conversation-deleted', { conversationId });
    },
    [removeConversation],
  );

  const duplicateCardRef = useRef<DuplicateCardRef>(null);
  const duplicateCardInitRef = useRef<((conv: any) => void) | null>(null);

  const handleDuplicateConversation = useCallback((_e: any, conv: any) =>
  {
    duplicateCardInitRef.current?.(conv);
    duplicateCardRef.current?.openModal();
  }, []);

  // ==================== Bus Subscriptions ====================

  const updateConversationStateRef = useRef(updateConversationState);
  updateConversationStateRef.current = updateConversationState;
  const removeConversationRef = useRef(removeConversation);
  removeConversationRef.current = removeConversation;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const updateFieldRef = useRef(updateField);
  updateFieldRef.current = updateField;

  useEffect(() =>
  {
    const hot = () => hotRef.current?.hotInstance;

    const setDataAtRowProp = (conversationId: number, prop: string, value: any) =>
    {
      const h = hot();
      if (!h) return;
      const physRow = activeRowsRef.current.findIndex(r => r?.conversationId === conversationId);
      if (physRow < 0) return;
      const visRow =
        typeof h.toVisualRow === 'function' ? (h.toVisualRow(physRow) ?? physRow) : physRow;
      h.setDataAtRowProp(visRow, prop, value, 'bus-update');
    };

    const unsubs = [
      conversationBus.on('title-updated', ({ conversationId, title }) =>
      {
        updateConversationStateRef.current(conversationId, { Title: title });
        setDataAtRowProp(conversationId, 'col-title', title);
      }),
      conversationBus.on('redflag-updated', ({ conversationId, isRedFlag }) =>
      {
        updateConversationStateRef.current(conversationId, { IsRedFlag: isRedFlag });
      }),
      conversationBus.on('important-updated', ({ conversationId, isImportant }) =>
      {
        updateConversationStateRef.current(conversationId, { IsImportant: isImportant });
      }),
      conversationBus.on('conversation-deleted', ({ conversationId }) =>
      {
        removeConversationRef.current(conversationId);
      }),
      conversationBus.on('layer-updated', ({ conversationId, layerId, layerData }) =>
      {
        const customFields: any[] = layerData?.CustomFields ?? [];
        customFields.forEach((cf: any) =>
        {
          const cfId = cf.CustomFieldId;
          const value = cf.Value ?? cf.FieldValue;
          const display = cf.SelectedValuesText ?? String(value ?? '');
          updateFieldRef.current(conversationId, layerId, cfId, value, display);
        });
      }),
      conversationBus.on('layers-updated', ({ conversationId, layers }) =>
      {
        updateConversationStateRef.current(conversationId, { ConversationLayers: layers as any });
        // GridTitleCell reads ConversationLayers from GridViewContext, so the context
        // update above is sufficient — no HOT setDataAtRowProp needed.
      }),
    ];

    return () => unsubs.forEach(fn => fn());
  }, []);

  const actionMenuConfig = useMemo(() => buildActionMenuConfig(), []);

  // Context value — onOpenInfoWindow receives the full ConversationDetail
  // (which now includes ConversationPhaseId) so callers can open the correct phase tab.
  const contextValue = useMemo(
    () => ({
      conversations: activeConversations,
      onOpenInfoWindow,
      onUpdateConversation,
      onRenameTitle: handleRenameTitle,
      onRedFlagToggle: handleRedFlagToggle,
      onImportantToggle: handleImportantToggle,
      onDeleteConversation: handleDeleteConversation,
      onDuplicateConversation: handleDuplicateConversation,
      actionMenuConfig,
    }),
    [
      activeConversations,
      onOpenInfoWindow,
      onUpdateConversation,
      handleRenameTitle,
      handleRedFlagToggle,
      handleImportantToggle,
      handleDeleteConversation,
      handleDuplicateConversation,
      actionMenuConfig,
    ],
  );

  // ==================== Column Headers ====================

  const colHeaders = useCallback(
    (col: number): string =>
    {
      const column = visibleColumns[col];
      if (!column) return '';
      return buildColumnHeader(column);
    },
    [visibleColumns],
  );

  const handleAfterGetColHeader = useCallback(
    (col: number, TH: HTMLTableCellElement) =>
    {
      const column = visibleColumns[col];

      if (!column?.layerColor)
      {
        TH.querySelector('.gridview-col-header-color-bar')?.remove();
      }
      else
      {
        let bar = TH.querySelector<HTMLElement>('.gridview-col-header-color-bar');
        if (!bar)
        {
          bar = document.createElement('div');
          bar.className = 'gridview-col-header-color-bar';
          TH.insertBefore(bar, TH.firstChild);
        }
        bar.style.backgroundColor = column.layerColor;
      }

      if (!column) return;

      TH.dataset.sortColKey = column.key;

      if (!TH.querySelector('.gridview-sort-icon'))
      {
        const icon = document.createElement('span');
        icon.className = 'gridview-sort-icon';
        const colHeader = TH.querySelector('.colHeader');
        (colHeader ?? TH).appendChild(icon);
      }
    },
    [visibleColumns],
  );

  // Delegated sort click listener
  useEffect(() =>
  {
    const container = containerRef.current;
    if (!container) return;
    const handleHeaderClick = (e: MouseEvent) =>
    {
      const th = (e.target as Element).closest('thead th') as HTMLTableCellElement | null;
      if (!th) return;
      const colKey = th.dataset.sortColKey;
      if (!colKey) return;
      setSortState(prev =>
      {
        if (!prev || prev.key !== colKey) return { key: colKey, dir: 'asc' };
        if (prev.dir === 'asc') return { key: colKey, dir: 'desc' };
        return null;
      });
    };
    container.addEventListener('click', handleHeaderClick);
    return () => container.removeEventListener('click', handleHeaderClick);
  }, []);

  // Update sort icon data-dir on sortState change
  useEffect(() =>
  {
    const container = containerRef.current;
    if (!container) return;
    container.querySelectorAll<HTMLElement>('.gridview-sort-icon').forEach(icon =>
    {
      const th = icon.closest('th') as HTMLTableCellElement | null;
      const colKey = th?.dataset.sortColKey;
      if (!colKey) return;
      if (sortState?.key === colKey)
      {
        icon.dataset.dir = sortState.dir;
      }
      else
      {
        delete icon.dataset.dir;
      }
    });
  }, [sortState]);

  // ==================== Cell Properties ====================

  const cells = useCallback(
    (row: number, col: number) =>
    {
      if (col === 0) return { readOnly: true };
      // Created-at column: display-only
      if (col === 1) return { readOnly: true };

      const column = visibleColumns[col];
      if (!column) return {};

      const hotInstance = hotRef.current?.hotInstance;
      const physicalRow =
        hotInstance && typeof hotInstance.toPhysicalRow === 'function'
          ? (hotInstance.toPhysicalRow(row) ?? row)
          : row;

      const conversation = activeConversations[physicalRow];

      // Mixed phase and card rows: decide per row, and exclude aggregates per field.
      if (!conversation || !isCellEnabled(conversation, column, { allowPhaseRows: true }))
      {
        return { readOnly: true, className: 'gridview-cell-disabled' };
      }

      if (
        isCellSummatory(conversation, column) ||
        isPhaseAggregateCell(conversation, column, isAdmin)
      )
      {
        return { readOnly: true, className: 'gridview-cell-disabled' };
      }

      if (column.fieldTypeId != null && !GRID_EDITABLE_FIELD_TYPES.has(column.fieldTypeId))
      {
        return { readOnly: true, className: 'gridview-cell-disabled' };
      }

      return {};
    },
    [visibleColumns, activeConversations, isAdmin],
  );

  // ==================== Editing Intercept ====================

  const handleBeforeBeginEditing = useCallback(
    (visualRow: number, visualCol: number): boolean =>
    {
      if (visualCol === 0) return false;
      // Col 1 (created-at): read-only
      if (visualCol === 1) return false;

      const column = visibleColumns[visualCol];
      if (!column) return false;

      if (column.fieldTypeId == null || !GRID_EDITABLE_FIELD_TYPES.has(column.fieldTypeId))
      {
        return false;
      }

      const hot = hotRef.current?.hotInstance;
      if (!hot) return false;

      const physicalRow =
        typeof hot.toPhysicalRow === 'function'
          ? (hot.toPhysicalRow(visualRow) ?? visualRow)
          : visualRow;

      const conversation = activeConversations[physicalRow];
      if (!conversation || !isCellEnabled(conversation, column, { allowPhaseRows: true }))
      {
        return false;
      }
      if (isCellSummatory(conversation, column)) return false;
      if (isPhaseAggregateCell(conversation, column, isAdmin)) return false;

      const td = hot.getCell(visualRow, visualCol) as HTMLTableCellElement | null;
      const cellRect = td?.getBoundingClientRect() ?? { top: 0, left: 0, width: 200, height: 30 };

      const layer = conversation.LayerCustomFields?.find(l => l.LayerId === column.layerId);
      const ids =
        column.customFieldIds ?? (column.customFieldId != null ? [column.customFieldId] : []);
      const cf = layer?.CustomFields?.find(f => ids.includes(f.CustomFieldId));

      setEditingCell({
        visualRow,
        visualCol,
        column,
        conversation,
        customField: cf,
        currentValue: cf?.FieldValue ?? '',
        cellRect: {
          top: cellRect.top,
          left: cellRect.left,
          width: cellRect.width,
          height: cellRect.height,
        },
      });

      return false;
    },
    [visibleColumns, activeConversations, isAdmin],
  );

  // ==================== Infinite Scroll ====================

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const loadingMoreRef = useRef(loadingMore);
  loadingMoreRef.current = loadingMore;
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  useEffect(() =>
  {
    const checkScroll = (target: HTMLElement) =>
    {
      if (!hasMoreRef.current || loadingMoreRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = target;
      if (scrollHeight > clientHeight && scrollHeight - scrollTop - clientHeight < 300)
      {
        loadMoreRef.current();
      }
    };

    const onScroll = (e: Event) =>
    {
      const target = e.target as HTMLElement;
      if (!target?.classList.contains('wtHolder')) return;
      if (!containerRef.current?.contains(target)) return;
      checkScroll(target);
    };

    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  const handleAfterScrollVertically = useCallback(() =>
  {
    if (!hasMoreRef.current || loadingMoreRef.current) return;
    const holder = containerRef.current?.querySelector<HTMLElement>('.ht_master .wtHolder');
    if (!holder) return;
    const { scrollTop, scrollHeight, clientHeight } = holder;
    if (scrollHeight > clientHeight && scrollHeight - scrollTop - clientHeight < 300)
    {
      loadMoreRef.current();
    }
  }, []);

  // ==================== Fill / Paste ====================

  const handleBeforeChange = useCallback(
    (changes: [number, string | number, any, any][], source: string) =>
    {
      if (source === 'Autofill.fill')
      {
        for (let i = 0; i < changes.length; i++)
        {
          const change = changes[i];
          if (change && change[3] == null) (changes as any)[i] = null;
        }
      }
    },
    [],
  );

  /**
   * Batch-saves fill-handle drag and copy-paste changes.
   * Uses CoreTaskPhaseId (via the PhaseRow) for each row's save payload.
   */
  const handleFillRange = useCallback(
    async (changes: [number, string | number, any, any][]) =>
    {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;

      setFillSaving(true);

      const getOptions = async (
        column: GridColumn,
        conversationConfigValueId?: number,
      ): Promise<FieldOption[]> =>
      {
        const cfgId =
          column.fieldTypeId === FIELD_TYPES.MULTI_SELECT
            ? (conversationConfigValueId ?? configValueId)
            : configValueId;
        const cacheKey = `${column.customFieldId}-${cfgId ?? 0}-${column.layerId ?? 0}`;
        if (!optionsCacheRef.current.has(cacheKey))
        {
          let opts: FieldOption[];
          if (column.serviceUrl)
          {
            const isPersonMulti = column.fieldTypeId === FIELD_TYPES.MULTI_SELECT;
            opts = await gridViewService.getFieldOptionsByServiceUrl(
              column.serviceUrl,
              isPersonMulti
                ? {
                  configValueId: cfgId,
                  layerId: column.layerId,
                  projectId: projectId ? parseInt(projectId, 10) : undefined,
                }
                : {},
              column.textField,
              column.valueField,
            );
          }
          else
          {
            opts = await gridViewService.getFieldOptions(column.customFieldId!, {
              configValueId: cfgId,
              layerId: column.layerId,
              projectId: projectId ? parseInt(projectId, 10) : undefined,
            });
          }
          optionsCacheRef.current.set(cacheKey, opts);
        }
        return optionsCacheRef.current.get(cacheKey)!;
      };

      const saveRequests: SaveMultipleFieldRequest[] = [];
      const saveTypes: (1 | 2 | string)[] = [];
      const displayUpdates: Array<{ visualRow: number; prop: string; display: string }> = [];
      const fieldUpdates: Array<{
        conversationId: number;
        coreTaskId?: number;
        conversationPhaseId?: number | null;
        layerId: number;
        customFieldId: number;
        value: any;
        display: string;
      }> = [];

      for (const [visualRow, prop, _oldVal, newVal] of changes)
      {
        if (newVal == null) continue;

        const key = String(prop);
        if (key === 'col-title' || key === 'col-created-at') continue;

        const column = columnMap.get(key);
        if (!column?.customFieldId || column.fieldTypeId == null) continue;
        if (!GRID_EDITABLE_FIELD_TYPES.has(column.fieldTypeId)) continue;

        const physicalRow =
          typeof hot.toPhysicalRow === 'function'
            ? (hot.toPhysicalRow(visualRow) ?? visualRow)
            : visualRow;

        const conversation = activeConversations[physicalRow];
        if (!conversation || !isCellEnabled(conversation, column, { allowPhaseRows: true }))
        {
          continue;
        }
        if (isCellSummatory(conversation, column)) continue;
        if (isPhaseAggregateCell(conversation, column, isAdmin)) continue;

        const layer = conversation.LayerCustomFields?.find(l => l.LayerId === column.layerId);
        if (!layer) continue;

        const cfIds =
          column.customFieldIds ?? (column.customFieldId != null ? [column.customFieldId] : []);
        const matchingCfs = layer.CustomFields?.filter(f => cfIds.includes(f.CustomFieldId)) ?? [];
        if (matchingCfs.length === 0) continue;

        // Phase-aware CoreTaskId
        const phaseRow = activeRows[physicalRow] as PhaseRow | undefined;
        const coreTaskId =
          phaseRow?.coreTaskPhaseId ??
          (conversation as any).CoreTaskPhaseId ??
          conversation.CoreTaskId ??
          conversation.ConversationId;

        let saveValue: any = String(newVal);
        let displayValue: string = String(newVal);

        const isMulti = column.fieldTypeId === FIELD_TYPES.MULTI_SELECT;
        const isCombo =
          column.fieldTypeId === FIELD_TYPES.COMBO || column.fieldTypeId === FIELD_TYPES.COMBO_ALT;
        const isInteger = column.fieldTypeId === FIELD_TYPES.INTEGER;
        const isNumeric =
          isInteger ||
          column.fieldTypeId === FIELD_TYPES.DECIMAL ||
          column.fieldTypeId === FIELD_TYPES.INTEGER_ALT;

        if (isMulti || isCombo)
        {
          const options = await getOptions(column, (conversation as any).ConfigValueId);

          if (isMulti)
          {
            const names = String(newVal)
              .split(',')
              .map(s => s.trim())
              .filter(Boolean);
            const matched = names
              .map(name =>
                options.find(o => String(o.Text ?? '').toLowerCase() === name.toLowerCase()),
              )
              .filter((o): o is FieldOption => o != null);
            if (matched.length === 0) continue;
            saveValue = matched.map(o => o.Id).join(',');
            displayValue = matched.map(o => o.Text).join(', ');
          }
          else
          {
            const name = String(newVal).trim();
            const match = options.find(
              o => String(o.Text ?? '').toLowerCase() === name.toLowerCase(),
            );
            if (!match) continue;
            saveValue = String(match.Id);
            displayValue = String(match.Text);
          }
        }
        else if (isNumeric)
        {
          const strVal = String(newVal).trim().replace(',', '.');
          const num = isInteger ? parseInt(strVal, 10) : parseFloat(strVal);
          if (isNaN(num)) continue;
          if (isInteger && !Number.isInteger(parseFloat(strVal))) continue;
          saveValue = num;
          displayValue = String(num);
        }

        for (const cf of matchingCfs)
        {
          if (column.serviceUrl)
          {
            saveRequests.push({
              ...cf,
              CustomFieldValueId: cf.CustomFieldValueId ?? 0,
              CustomFieldId: cf.CustomFieldId,
              CoreTaskId: coreTaskId,
              ConversationId: conversation.ConversationId,
              Value: saveValue,
              PreviousValue: null,
              SelectedValuesText: displayValue,
              LayerId: column.layerId,
              SocketConnectionId: '',
            });
            saveTypes.push('savePost');
          }
          else if (isNumeric)
          {
            const cfValueId = cf.CustomFieldValueId ?? 0;
            saveRequests.push({
              CustomFieldValueId: cfValueId,
              CustomFieldId: cf.CustomFieldId,
              FieldValue: displayValue,
              Value: saveValue,
              FieldName: cf.FieldName,
              FieldTypeId: cf.FieldTypeId,
              LayerId: column.layerId ?? cf.LayerId,
              ConversationId: conversation.ConversationId,
              ServiceUrl: cf.ServiceUrl ?? '',
              TextField: cf.TextField ?? '',
              ValueField: cf.ValueField ?? '',
              OrderInLayer: cf.OrderInLayer,
              CoreTaskId: coreTaskId,
              PreviousValue: undefined,
              SelectedValuesText: undefined,
              SocketConnectionId: '',
            });
            saveTypes.push(cfValueId > 0 ? 'save' : 'savePost');
          }
          else
          {
            saveRequests.push({
              CustomFieldValueId: cf.CustomFieldValueId ?? 0,
              CustomFieldId: cf.CustomFieldId,
              CoreTaskId: coreTaskId,
              ConversationId: null,
              Value: saveValue,
              SelectedValuesText: displayValue,
              LayerId: column.layerId,
              SocketConnectionId: '',
            });
            saveTypes.push((cf.CustomFieldValueId ?? 0) > 0 ? 2 : 1);
          }
          fieldUpdates.push({
            conversationId: conversation.ConversationId,
            coreTaskId: coreTaskId !== conversation.ConversationId ? coreTaskId : undefined,
            conversationPhaseId: conversation.ConversationPhaseId,
            layerId: column.layerId!,
            customFieldId: cf.CustomFieldId,
            value: saveValue,
            display: displayValue,
          });
        }
        displayUpdates.push({ visualRow, prop: key, display: displayValue });
      }

      if (saveRequests.length === 0)
      {
        setFillSaving(false);
        return;
      }

      displayUpdates.forEach(({ visualRow, prop, display }) =>
      {
        hot.setDataAtRowProp(visualRow, prop, display, 'fill-save');
      });

      const ok = await gridViewService.saveMultipleFieldValues(saveRequests, saveTypes);

      if (ok)
      {
        fieldUpdates.forEach(
          ({ conversationId, conversationPhaseId, layerId, customFieldId, value, display }) =>
          {
            updateField(
              conversationId,
              layerId,
              customFieldId,
              value,
              display,
              conversationPhaseId,
            );
          },
        );
        const notifiedIds = new Set<number>();
        fieldUpdates.forEach(({ conversationId, coreTaskId, layerId, value }) =>
        {
          if (!notifiedIds.has(conversationId))
          {
            notifiedIds.add(conversationId);
            conversationBus.emit('field-updated', {
              conversationId,
              coreTaskId,
              fieldName: '',
              value,
              layerId,
            });
          }
        });
      }
      else
      {
        console.error('[PhaseDetails] Fill/paste batch save failed');
      }

      setFillSaving(false);
    },
    [columnMap, activeConversations, activeRows, configValueId, projectId, updateField, isAdmin],
  );

  const handleAfterCopy = useCallback(() =>
  {
    if (copyMsgTimerRef.current) clearTimeout(copyMsgTimerRef.current);
    setShowCopyMsg(true);
    copyMsgTimerRef.current = setTimeout(() => setShowCopyMsg(false), 2000);
  }, []);

  const handleAfterChange = useCallback(
    (changes: [number, string | number, any, any][] | null, source: string) =>
    {
      if (!changes || source === 'loadData') return;
      if (source === 'save-field' || source === 'title-rename' || source === 'fill-save') return;
      if (source === 'Autofill.fill' || source === 'CopyPaste.paste')
      {
        handleFillRange(changes);
      }
    },
    [handleFillRange],
  );

  const handleBeforeOnCellMouseDown = useCallback((_e: MouseEvent, coords: any): boolean | void =>
  {
    if (coords?.row === -1) return false;
  }, []);

  const handleAfterOnCellMouseDown = useCallback((_e: MouseEvent, coords: any) =>
  {
    if (coords?.row === -1)
    {
      hotRef.current?.hotInstance?.deselectCell();
    }
  }, []);

  // ==================== Context Menu ====================

  const handleAfterOnCellContextMenu = useCallback(
    (event: MouseEvent, coords: { row: number; col: number }) =>
    {
      event.preventDefault();
      const col = visibleColumns[coords.col] ?? null;
      setContextMenu({ x: event.clientX, y: event.clientY, column: col });
    },
    [visibleColumns],
  );

  const handleHideColumn = useCallback(async () =>
  {
    if (!contextMenu?.column) return;
    setContextMenu(null);
    await hideColumn(getColumnApiKey(contextMenu.column));
    refresh();
  }, [contextMenu, hideColumn, refresh]);

  const handleShowColumn = useCallback(
    async (key: string) =>
    {
      setContextMenu(null);
      await showColumn(key);
      refresh();
    },
    [showColumn, refresh],
  );

  const handleApplyManageColumns = useCallback(
    async (newHiddenKeys: string[]) =>
    {
      await applyHiddenColumns(newHiddenKeys);
      refresh();
    },
    [applyHiddenColumns, refresh],
  );

  // ==================== Export ====================

  const stripHtmlForExport = (value: string): string =>
  {
    return value
      .replace(/<p>/gi, '')
      .replace(/<\/p>/gi, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/a>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .trim();
  };

  const handleExport = useCallback(async () =>
  {
    const countY = visibleColumns.length;
    const countX = activeRows.length;
    const totalSheetCols = countY + 2;
    const webUrl = import.meta.env.VITE_WEB_URL || '';

    const toArgb = (color: string): string =>
    {
      const hex = color.replace('#', '').trim();
      if (/^[0-9A-Fa-f]{6}$/.test(hex)) return `FF${hex.toUpperCase()}`;
      if (/^[0-9A-Fa-f]{3}$/.test(hex))
      {
        const [r, g, b] = hex.split('');
        return `FF${r}${r}${g}${g}${b}${b}`.toUpperCase();
      }
      return 'FF000000';
    };

    const getColForIdx = (col: number): GridColumn | null =>
    {
      if (col === 1) return visibleColumns[0] ?? null;
      if (col === 2) return null;
      if (col === totalSheetCols) return null;
      return visibleColumns[col - 2] ?? null;
    };

    const workbook = new ExcelJS.Workbook();
    const sheetName = sanitizeSheetName(dashboardName, 'Phase Details');
    const sheet = workbook.addWorksheet(sheetName);

    for (let col = 1; col <= totalSheetCols; col++)
    {
      let width = 20;
      if (col === 1) width = 40;
      else if (col === 2) width = 14;
      else if (col === 3) width = 14;
      else if (col === totalSheetCols) width = 50;
      sheet.getColumn(col).width = width;
    }

    const layerRow = sheet.addRow(new Array(totalSheetCols).fill(''));
    layerRow.height = 18;

    let grpStart = -1;
    let grpLayerId: number | undefined;
    let grpLayerName = '';
    let grpLayerColor = '';

    const flushGroup = (endCol: number) =>
    {
      if (grpStart < 0) return;
      const cell = layerRow.getCell(grpStart);
      cell.value = grpLayerName;
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(grpLayerColor) } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      if (endCol > grpStart)
      {
        sheet.mergeCells(1, grpStart, 1, endCol);
      }
      grpStart = -1;
    };

    for (let col = 1; col <= totalSheetCols; col++)
    {
      const gc = getColForIdx(col);
      const lid = gc?.layerId;
      if (lid != null)
      {
        if (grpStart >= 0 && grpLayerId === lid)
        {
          // extend group
        }
        else
        {
          flushGroup(col - 1);
          grpStart = col;
          grpLayerId = lid;
          grpLayerName = gc!.layerName ?? '';
          grpLayerColor = gc!.layerColor ?? '';
        }
      }
      else
      {
        flushGroup(col - 1);
      }
    }
    flushGroup(totalSheetCols);

    const headerValues: string[] = [];
    for (let y = 0; y < countY; y++)
    {
      headerValues.push(visibleColumns[y]?.title ?? '');
      if (y === 0) headerValues.push('Card Type');
    }
    headerValues.push('Link');

    const headerRow = sheet.addRow(headerValues);
    headerRow.height = 18;
    headerRow.eachCell((cell, col) =>
    {
      const gc = getColForIdx(col);
      cell.font = {
        bold: true,
        size: 10,
        color: { argb: gc?.layerColor ? toArgb(gc.layerColor) : 'FF000000' },
      };
      cell.border = { bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } } };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
    });

    sheet.autoFilter = {
      from: { row: 2, column: 1 },
      to: { row: 2, column: totalSheetCols },
    };

    for (let x = 0; x < countX; x++)
    {
      const conv = activeConversations[x];
      const cardType = (conv as any)?.SideMenuText ?? '';
      const rowValues: (string | number | null)[] = [];

      for (let y = 0; y < countY; y++)
      {
        const col = visibleColumns[y]!;
        let cellValue = String(activeRows[x]?.[col.key] ?? '');
        if (cellValue && isNaN(Number(cellValue)))
        {
          cellValue = stripHtmlForExport(cellValue);
        }
        rowValues.push(cellValue || null);
        if (y === 0) rowValues.push(cardType || null);
      }

      const convId = conv?.ConversationId ?? '';
      rowValues.push(convId ? `${webUrl}Dashboard/ConversationDetails/#/${convId}` : null);
      sheet.addRow(rowValues);
    }

    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const fileName = `PhaseDetails_${mm}-${dd}-${d.getFullYear()}`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [visibleColumns, activeRows, activeConversations, dashboardName]);

  useImperativeHandle(ref, () => ({ exportToExcel: handleExport }), [handleExport]);

  // ==================== Render Hooks ====================

  const handleAfterRender = useCallback(() =>
  {
    const container = containerRef.current;
    if (!container) return;
    container.querySelectorAll<HTMLElement>('.handsontable thead tr th').forEach(th =>
    {
      th.style.height = `${GRID_HEADER_HEIGHT}px`;
      th.style.maxHeight = `${GRID_HEADER_HEIGHT}px`;
      th.style.overflow = 'hidden';
    });
  }, []);

  const handleAfterRenderer = useCallback(
    (TD: HTMLTableCellElement, _row: number, _col: number, _prop: string | number, value: any) =>
    {
      const text = value != null ? String(value).trim() : '';
      if (text)
      {
        TD.title = text;
      }
      else
      {
        TD.removeAttribute('title');
      }
    },
    [],
  );

  // ==================== Render ====================

  const hotHeight = containerHeight > 0 ? containerHeight : '100%';
  const dynamicColumns = visibleColumns.slice(2);

  return (
    <div ref={containerRef} className="phase-details-container">
      {loading ? (
        <GridViewSkeleton />
      ) : rows.length === 0 ? (
        <div className="phase-details-empty">This dashboard doesn't have any data to display.</div>
      ) : (
        <>
          <GridViewContext.Provider value={contextValue}>
            <HotTable
              ref={hotRef}
              data={activeRows}
              rowHeaders={true}
              rowHeights={30}
              columnHeaderHeight={GRID_HEADER_HEIGHT}
              licenseKey={GRID_LICENSE_KEY}
              themeName="ht-theme-main"
              fixedColumnsStart={1}
              manualColumnResize={true}
              manualRowResize={false}
              autoRowSize={false}
              autoColumnSize={false}
              viewportRowRenderingOffset={30}
              viewportColumnRenderingOffset={2}
              fillHandle={{ direction: 'vertical', autoInsertRow: false }}
              selectionMode="multiple"
              outsideClickDeselects={false}
              height={hotHeight}
              stretchH="none"
              colHeaders={colHeaders}
              cells={cells}
              afterGetColHeader={handleAfterGetColHeader as any}
              beforeChange={handleBeforeChange as any}
              beforeBeginEditing={handleBeforeBeginEditing as any}
              beforeOnCellMouseDown={handleBeforeOnCellMouseDown as any}
              afterOnCellMouseDown={handleAfterOnCellMouseDown as any}
              afterScrollVertically={handleAfterScrollVertically}
              afterChange={handleAfterChange as any}
              afterCopy={handleAfterCopy as any}
              afterRender={handleAfterRender}
              afterRenderer={handleAfterRenderer as any}
              afterOnCellContextMenu={handleAfterOnCellContextMenu as any}
            >
              {/* Fixed col 0: title with React renderer */}
              <HotColumn data="col-title" width={GRID_TITLE_COLUMN_WIDTH}>
                <GridTitleCell hot-renderer />
              </HotColumn>

              {/* Fixed col 1: creation date — read-only */}
              <HotColumn data="col-created-at" width={GRID_DEFAULT_COLUMN_WIDTH} />

              {/* Dynamic columns */}
              {dynamicColumns.map(col => (
                <HotColumn
                  key={col.key}
                  data={col.key}
                  width={col.width ?? GRID_DEFAULT_COLUMN_WIDTH}
                />
              ))}
            </HotTable>

            {fillSaving && <div className="phase-details-loading-overlay">Saving fields…</div>}

            {loadingMore && <div className="phase-details-loading-more">Loading more…</div>}

            {!hasMore && !loadingMore && conversations.length > 0 && (
              <div className="phase-details-no-more-items">No More Items</div>
            )}

            {editingCell && (
              <GridCellEditor
                editingCell={editingCell}
                onSave={handleCellSave}
                onCancel={() => setEditingCell(null)}
                configValueId={configValueId}
                projectId={projectId}
                getCellRect={getEditingCellRect}
                onCommitWithEnter={handleCommitWithEnter}
              />
            )}

            {contextMenu && (
              <GridContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                targetColumn={contextMenu.column}
                allDynamicColumns={columns.slice(2)}
                hiddenKeys={hiddenKeys}
                onHide={handleHideColumn}
                onShow={handleShowColumn}
                onManageColumns={() =>
                {
                  setContextMenu(null);
                  setManageColumnsOpen(true);
                }}
                onClose={() => setContextMenu(null)}
              />
            )}

            <ManageColumnsModal
              open={manageColumnsOpen}
              onClose={() => setManageColumnsOpen(false)}
              allDynamicColumns={columns.slice(2)}
              hiddenKeys={hiddenKeys}
              onApply={handleApplyManageColumns}
            />
          </GridViewContext.Provider>

          <DuplicateCard
            ref={duplicateCardRef}
            config={{
              apiUrl: actionMenuConfig.api.url,
              authToken: actionMenuConfig.api.token,
              SocketConnectionId: socketService.connectionId,
              attachReactFunctions: (fns: any) =>
              {
                duplicateCardInitRef.current = fns.initConfig;
              },
              DashboardId: 0,
              functions: {
                loadingBarStart: () => {},
                loadingBarComplete: () => {},
                addConversation: () =>
                {
                  refresh();
                },
              },
              params: { Conversation: {} },
            }}
            UpdateCustomFields={() => {}}
          />
        </>
      )}

      {showCopyMsg &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: '64px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#62accb',
              color: '#fff',
              padding: '8px 20px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 500,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              pointerEvents: 'none',
              zIndex: 9999,
              whiteSpace: 'nowrap',
            }}
          >
            Value has been copied to clipboard.
          </div>,
          document.body,
        )}
    </div>
  );
});

export default PhaseDetails;
